import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Card, CardContent } from '@/Domain/components/Shared/Card/Card';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { QuickchatAiIcon } from '@/Domain/components/icons/QuickchatAiWordmark.icon';
import { useLogout } from '@/Domain/hooks/Auth/useLogout';

export function ShopifyConnectAgentPage() {
  const { t } = useTranslation('shopify_connect');
  const [searchParams] = useSearchParams();
  const shop = searchParams.get('shop') ?? '';
  const eligibleScenarioIds = new Set(
    (searchParams.get('scenarios') ?? '').split(',').filter(Boolean),
  );

  const assistantsQuery = useQuery<AIAssistantsData>({ queryKey: [QueryKey.aiAssistants.get] });
  // Shopify's HMAC-protected install entry point cannot be resumed after signing out.
  const { handleLogout, isLoggingOut } = useLogout();
  const startOauth = useMutation<{ url: string }, unknown, { shopUrl: string; scenarioId: string }>(
    {
      mutationKey: [QueryKey.shopify.startOauth],
      onSuccess: ({ url }) => window.location.assign(url),
    },
  );
  const isBusy = startOauth.isPending || isLoggingOut;

  // No shop means we're not inside a Shopify install flow; there's nothing to connect.
  useEffect(() => {
    if (!shop) {
      window.location.assign('/');
    }
  }, [shop]);

  if (!shop) {
    return null;
  }

  const agents = (assistantsQuery.data?.all_ai_assistants ?? []).filter((agent) =>
    eligibleScenarioIds.has(agent.scenario_id),
  );

  const handleSelect = (scenarioId: string): void => {
    if (isBusy) {
      return;
    }
    startOauth.mutate({ shopUrl: shop, scenarioId });
  };

  const handleSignOut = (): void => {
    if (isBusy) {
      return;
    }
    handleLogout();
  };

  if (assistantsQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="text-muted-foreground">{t('loading')}</div>
      </div>
    );
  }

  if (assistantsQuery.isError && !assistantsQuery.data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-foreground">
        <p className="text-muted-foreground">{t('error')}</p>
        <Button
          variant="dark"
          onClick={() => assistantsQuery.refetch()}
          data-testid="shopify-connect-retry"
        >
          {t('retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-background p-6 text-foreground">
      <QuickchatAiIcon
        className="absolute top-6 left-6 scale-[1.2] text-foreground"
        data-testid="shopify-connect-page-logo"
      />

      <div className="flex w-full max-w-xl flex-col gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-semibold">{t('title', { shop })}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>

        <div className="flex flex-col gap-3" data-testid="shopify-connect-agent-list">
          {agents.length === 0 && (
            <div
              className="flex flex-col items-center gap-4 rounded-xl border border-border p-8 text-center"
              data-testid="shopify-connect-empty"
            >
              <p className="text-muted-foreground">{t('empty')}</p>
            </div>
          )}
          {agents.map((agent) => (
            <Card key={agent.scenario_id} data-testid={`shopify-connect-card-${agent.scenario_id}`}>
              <CardContent className="flex items-center justify-between gap-4 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  {agent.avatar_image ? (
                    <img
                      src={agent.avatar_image}
                      alt=""
                      className="size-8 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <Bot className="size-8 shrink-0 rounded-full bg-muted p-1.5 text-muted-foreground" />
                  )}
                  <span className="truncate font-medium">
                    {agent.name || agent.fallback_name || t('untitled_agent')}
                  </span>
                </div>
                <Button
                  variant="dark"
                  disabled={isBusy}
                  onClick={() => handleSelect(agent.scenario_id)}
                  data-testid={`shopify-connect-select-${agent.scenario_id}`}
                >
                  {t('connect')}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {startOauth.isError && (
          <p className="text-center text-sm text-destructive" data-testid="shopify-connect-failed">
            {t('connect_failed')}
          </p>
        )}

        <div className="flex flex-col gap-2 border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground">{t('other_account_hint')}</p>
          <Button
            variant="ghost"
            disabled={isBusy}
            onClick={handleSignOut}
            data-testid="shopify-connect-sign-out"
          >
            {t('sign_out')}
          </Button>
        </div>
      </div>
    </div>
  );
}
